import Phaser from "phaser";
import { GAME } from "../config";
import { getMpClient } from "../net/MultiplayerClient";
import { addFullscreenButton } from "../ui/Fullscreen";

/**
 * Турнирная таблица. Может работать как screensaver — без UI, авто-возврат на тап.
 */
export class LeaderboardScene extends Phaser.Scene {
  private mp = getMpClient();
  private mpUnsub?: () => void;
  private screensaver = false;
  private rows: Phaser.GameObjects.Container[] = [];
  private statusText!: Phaser.GameObjects.Text;
  private refreshTimer?: Phaser.Time.TimerEvent;

  constructor() { super("Leaderboard"); }

  init(data: { screensaver?: boolean }) {
    this.screensaver = !!data?.screensaver;
    this.rows = [];
  }

  create() {
    const { WIDTH, HEIGHT } = GAME;

    const bg = this.add.graphics();
    bg.fillGradientStyle(0x1a0808, 0x000000, 0x080000, 0x000000, 1);
    bg.fillRect(0, 0, WIDTH, HEIGHT);

    this.add.text(WIDTH / 2, 30, "🏆 ТУРНИРНАЯ ТАБЛИЦА", {
      fontFamily: "Arial Black", fontSize: "26px",
      color: "#ffd700", stroke: "#c41e3a", strokeThickness: 4,
    }).setOrigin(0.5);

    this.statusText = this.add.text(WIDTH / 2, 60, "Загрузка...", {
      fontFamily: "Arial Black", fontSize: "13px", color: "#aaaaaa",
    }).setOrigin(0.5);

    if (!this.screensaver) {
      const back = this.add.text(60, 28, "← НАЗАД", {
        fontFamily: "Arial Black", fontSize: "14px",
        color: "#ffffff", backgroundColor: "#444444",
        padding: { x: 12, y: 6 } as any,
      } as any).setOrigin(0, 0.5).setInteractive({ useHandCursor: true });
      back.on("pointerdown", () => this.exit());
      this.input.keyboard!.on("keydown-ESC", () => this.exit());
      addFullscreenButton(this);
    } else {
      // screensaver: любой ввод возвращает в меню
      this.input.once("pointerdown", () => this.exit());
      this.input.keyboard!.once("keydown", () => this.exit());
      this.add.text(WIDTH / 2, HEIGHT - 20, "▶ Тапни / нажми любую клавишу", {
        fontFamily: "Arial Black", fontSize: "12px", color: "#666666",
      }).setOrigin(0.5);
    }

    this.mpUnsub = this.mp.on(e => {
      if (e.type === "leaderboard") this.renderRows(e.entries);
    });

    if (!this.mp.isOpen()) {
      this.mp.connect()
        .then(() => this.fetch())
        .catch(() => this.statusText.setColor("#ff4444").setText("Сервер недоступен"));
    } else {
      this.fetch();
    }

    // Регулярное обновление каждые 10с (и в screensaver, и в обычном режиме)
    this.refreshTimer = this.time.addEvent({
      delay: 10_000, loop: true, callback: () => this.fetch(),
    });
  }

  private fetch() {
    this.mp.fetchLeaderboard(10);
  }

  private renderRows(entries: { login: string; wins: number; losses: number }[]) {
    for (const r of this.rows) r.destroy();
    this.rows = [];

    if (entries.length === 0) {
      this.statusText.setColor("#888888").setText("Пока нет матчей. Сразись первым!");
      return;
    }
    this.statusText.setText("");

    const x0 = GAME.WIDTH / 2;
    let y = 100;

    // header
    const header = this.add.container(x0, y);
    const hbox = this.add.rectangle(0, 0, 720, 36, 0x2a0a0a).setStrokeStyle(2, 0xffd700);
    const hRank = this.add.text(-340, 0, "#",        { fontFamily: "Arial Black", fontSize: "14px", color: "#ffd700" }).setOrigin(0, 0.5);
    const hName = this.add.text(-280, 0, "БОЕЦ",     { fontFamily: "Arial Black", fontSize: "14px", color: "#ffd700" }).setOrigin(0, 0.5);
    const hWin  = this.add.text(180,  0, "ПОБЕДЫ",   { fontFamily: "Arial Black", fontSize: "14px", color: "#88ff88" }).setOrigin(0, 0.5);
    const hLos  = this.add.text(280,  0, "ПРОИГРЫШИ",{ fontFamily: "Arial Black", fontSize: "14px", color: "#ff8888" }).setOrigin(0, 0.5);
    header.add([hbox, hRank, hName, hWin, hLos]);
    this.rows.push(header);

    y += 40;
    entries.forEach((entry, i) => {
      const row = this.add.container(x0, y);
      const isMe = entry.login === this.mp.login;
      const fill = i === 0 ? 0x3a2a05 : i === 1 ? 0x2a2a2a : 0x1a0808;
      const stroke = isMe ? 0x88ff88 : 0x666666;
      const box = this.add.rectangle(0, 0, 720, 30, fill).setStrokeStyle(2, stroke);
      const medal = ["🥇","🥈","🥉"][i] || `${i+1}.`;
      const r1 = this.add.text(-340, 0, String(medal), { fontFamily: "Arial Black", fontSize: "16px", color: "#ffd700" }).setOrigin(0, 0.5);
      const r2 = this.add.text(-280, 0, entry.login.toUpperCase(), { fontFamily: "Arial Black", fontSize: "16px", color: isMe ? "#88ff88" : "#ffffff" }).setOrigin(0, 0.5);
      const r3 = this.add.text(180,  0, String(entry.wins),   { fontFamily: "Arial Black", fontSize: "16px", color: "#88ff88" }).setOrigin(0, 0.5);
      const r4 = this.add.text(280,  0, String(entry.losses), { fontFamily: "Arial Black", fontSize: "16px", color: "#ff8888" }).setOrigin(0, 0.5);
      row.add([box, r1, r2, r3, r4]);
      this.rows.push(row);
      y += 36;
    });
  }

  private exit() {
    this.refreshTimer?.remove();
    this.mpUnsub?.();
    this.scene.start("MainMenu");
  }
}
